import React from "react";
import CONTACT from "../../assets/images/contact.png";
import { FieldArray, FormikProps } from "formik";
import { useAppSelector } from "../../app/hooks";
import { IAdult, IReviewBookingDetails } from "./reviewbookingModel";
import { INITIALS } from "../../_constants/constant";
import moment from "moment";
import { getFieldError, getSelectedItems } from "../../_helpers/miscUtils";
import { handleUppercaseChange } from "../../_helpers/formatUtils";
import { FormField } from "../../components/formfield/FormField";

interface AdultFormProps {
  formik: FormikProps<IReviewBookingDetails>;
  AdultRequireDetails: any;
  ADULT: number;
  fieldRefs: React.MutableRefObject<any>;
  handleCheckboxChange: (
    type: "adults" | "children" | "infants",
    flightDetailsKey: "ADULT" | "CHILD" | "INFANT",
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
}

const AdultForm: React.FC<AdultFormProps> = ({
  formik,
  AdultRequireDetails,
  handleCheckboxChange,
  ADULT,
  fieldRefs,
}) => {
  const reviewBookingDetails = useAppSelector(
    (state) => state.reviewbooking.reviewBookingDetails
  );
  const CountryList = useAppSelector((state) => state.country.CountryList);

  const getAdultDisplayName = (adult: IAdult, index: number) => {
    return reviewBookingDetails?.adults[index]?.first_name
      ? `${adult.first_name} ${adult.last_name}`
      : `Adult ${
          index -
          (getSelectedItems(reviewBookingDetails?.adults, "first_name")
            ?.length -
            1)
        }`;
  };

  const renderTextInput = (
    index: number,
    fieldName: keyof IAdult,
    label: string,
    required: boolean,
    uppercase = false
  ) => {
    const fieldPath = `adults[${index}].${fieldName}`;
    const error = getFieldError<IAdult>(formik.errors.adults, index, fieldName);
    const touched = formik.touched.adults?.[index]?.[fieldName] as
      | boolean
      | undefined;

    return (
      <FormField
        label={label}
        required={required}
        error={error}
        touched={touched}
      >
        <input
          type="text"
          className={touched && error ? "error-border" : ""}
          name={fieldPath}
          // ref={(el) => (fieldRefs.current[fieldPath] = el)}
          onChange={
            uppercase
              ? (e) => handleUppercaseChange(formik, e)
              : formik.handleChange
          }
          onBlur={formik.handleBlur}
          value={formik.values.adults[index][fieldName] as string}
        />
      </FormField>
    );
  };

  const renderDateInput = (
    index: number,
    fieldName: keyof IAdult,
    label: string,
    required: boolean,
    minDate?: string,
    maxDate?: string
  ) => {
    const fieldPath = `adults[${index}].${fieldName}`;
    const error = getFieldError<IAdult>(formik.errors.adults, index, fieldName);
    const touched = formik.touched.adults?.[index]?.[fieldName] as
      | boolean
      | undefined;

    return (
      <FormField
        label={label}
        required={required}
        error={error}
        touched={touched}
      >
        <input
          type="date"
          name={fieldPath}
          // ref={(el) => (fieldRefs.current[fieldPath] = el)}
          onChange={formik.handleChange}
          min={minDate}
          max={maxDate}
          value={formik.values.adults[index][fieldName] as string}
          className={touched && error ? "error-border" : ""}
        />
      </FormField>
    );
  };

  const renderSelectInput = (
    index: number,
    fieldName: keyof IAdult,
    label: string,
    required: boolean,
    options: Array<{ value: string; label: string }>,
    placeholder: string
  ) => {
    const fieldPath = `adults[${index}].${fieldName}`;
    const error = getFieldError<IAdult>(formik.errors.adults, index, fieldName);
    const touched = formik.touched.adults?.[index]?.[fieldName] as
      | boolean
      | undefined;

    return (
      <FormField
        label={label}
        required={required}
        error={error}
        touched={touched}
      >
        <select
          name={fieldPath}
          // ref={(el) => (fieldRefs.current[fieldPath] = el)}
          value={formik.values.adults[index][fieldName] as string}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className={touched && error ? "error-border py-0" : "py-0"}
        >
          <option value="" hidden>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </FormField>
    );
  };

  const renderPersonalInfoSection = (index: number) => (
    <div className="field-set align-end">
      {renderSelectInput(
        index,
        "initial",
        "Initial",
        true,
        INITIALS.map((e) => ({ value: e, label: e })),
        "Select Initial"
      )}
      {renderTextInput(index, "first_name", "First Name", true, true)}
      {renderTextInput(index, "last_name", "Last Name", true, true)}
    </div>
  );

  const renderPassportInfoSection = (index: number) => (
    <div className="field-set">
      {renderDateInput(
        index,
        "date_of_birth",
        "Date of Birth",
        AdultRequireDetails?.DOB,
        "",
        moment().subtract(12, "years").format("YYYY-MM-DD")
      )}
      {AdultRequireDetails?.Passport_Number &&
        renderTextInput(index, "passport_no", "Passport No", true, true)}
      {!AdultRequireDetails?.Passport_Number && <div className="fields"></div>}
      {AdultRequireDetails?.Passport_Issuing_Country &&
        renderSelectInput(
          index,
          "issue_country_code",
          "Issue Country",
          true,
          CountryList?.map((e) => ({
            value: e.country_code,
            label: e.country_name,
          })),
          "Select Country"
        )}
      {!AdultRequireDetails?.Passport_Issuing_Country && (
        <div className="fields"></div>
      )}
    </div>
  );

  const renderAdditionalPassportInfoSection = (index: number) => (
    <div className="field-set mb-3">
      {AdultRequireDetails?.Passport_Expiry &&
        renderDateInput(
          index,
          "expire_date",
          "Passport Expiry Date",
          true,
          moment().add(6, "months").format("YYYY-MM-DD"),
          ""
        )}
      {AdultRequireDetails?.Nationality &&
        renderSelectInput(
          index,
          "nationality",
          "Nationality",
          true,
          CountryList?.map((e) => ({
            value: e.country_code,
            label: e.country_name,
          })),
          "Select Country"
        )}
      {AdultRequireDetails?.PanCard_No &&
        renderTextInput(index, "pancard_number", "Pancard No", true, true)}
      {!AdultRequireDetails?.PanCard_No && <div className="fields"></div>}
    </div>
  );

  const renderStudentInfoSection = (index: number) => (
    <div className="field-set mb-3">
      {AdultRequireDetails?.Student_Id &&
        renderDateInput(
          index,
          "student_id",
          "Student ID",
          true,
          "",
          moment().format("YYYY-MM-DD")
        )}
      <div className="fields"></div>
      <div className="fields"></div>
    </div>
  );

  const renderDefenceInfoSection = (index: number) => (
    <div className="field-set mb-3">
      {AdultRequireDetails?.DefenceServiceId &&
        renderDateInput(
          index,
          "defence_service_id",
          "Defence Service Id",
          true,
          "",
          moment().format("YYYY-MM-DD")
        )}
      {AdultRequireDetails?.DefenceIssueDate &&
        renderDateInput(
          index,
          "defence_issue_date",
          "Defence Issue Date",
          true,
          "",
          moment().format("YYYY-MM-DD")
        )}
      {AdultRequireDetails?.DefenceExpiryDate &&
        renderDateInput(
          index,
          "defence_expiry_date",
          "Defence Expiry Date",
          true,
          "",
          moment().format("YYYY-MM-DD")
        )}
      {!AdultRequireDetails?.DefenceExpiryDate && (
        <div className="fields"></div>
      )}
    </div>
  );

  return (
    <div className="adult-form">
      <div className="traveller-details-title">
        <p className="m-0">
          <img src={CONTACT} alt="contact" /> ADULT (12 Yrs+)
          <span className="m-0 tarveller-error-message">
            {formik.errors?.adultsLimit}
          </span>
        </p>
        <strong>
          {getSelectedItems(formik.values?.adults)?.length}/{ADULT} added
        </strong>
      </div>
      <span className="note">
        <strong>Important:</strong> Enter name as mentioned on your passport or
        Government approved IDs.
      </span>
      <FieldArray name="adults">
        {({ push, remove }) => (
          <>
            {formik.values.adults.map((adult, index) => (
              <div className="child-form" key={index}>
                <input
                  className="form-check"
                  type="checkbox"
                  name={`adults[${index}].isSelected`}
                  checked={formik.values.adults[index].isSelected}
                  onChange={(e) =>
                    handleCheckboxChange("adults", "ADULT", index, e)
                  }
                />
                <h5>{getAdultDisplayName(adult, index)}</h5>

                {formik.values.adults[index].isSelected && (
                  <div className={`form`}>
                    {renderPersonalInfoSection(index)}
                    {renderPassportInfoSection(index)}
                    {(AdultRequireDetails?.Passport_Number ||
                      AdultRequireDetails?.Passport_Expiry ||
                      AdultRequireDetails?.PanCard_No ||
                      AdultRequireDetails?.Nationality) &&
                      renderAdditionalPassportInfoSection(index)}
                    {AdultRequireDetails?.Student_Id &&
                      renderStudentInfoSection(index)}
                    {AdultRequireDetails?.DefenceServiceId &&
                      renderDefenceInfoSection(index)}
                  </div>
                )}
              </div>
            ))}
            <a
              className="add-more"
              href="javascript:void(0)"
              onClick={() => {
                if (ADULT > getSelectedItems(formik?.values?.adults)?.length) {
                  push({
                    first_name: "",
                    last_name: "",
                    initial: "",
                    date_of_birth: "",
                    passport_no: "",
                    issue_country_code: "",
                    nationality: "",
                    expire_date: "",
                    student_id: "",
                    defence_service_id: "",
                    defence_issue_date: "",
                    defence_expiry_date: "",
                    isSelected: true,
                  });
                } else {
                  formik.setFieldError(
                    "adultsLimit",
                    `You have already selected ${ADULT} ADULT. Remove before adding a new one.`
                  );
                }
              }}
            >
              + Add New Adult
            </a>
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default AdultForm;
